import { useState, useEffect, useRef } from 'react'

interface CountryPricing {
  totalPrice: string
  todayPrice: string
  countryName: string
  countryCode: string
  loading: boolean
}

const PRICING_MAP: Record<
  string,
  { totalPrice: string; todayPrice: string; countryName: string }
> = {
  UY: { totalPrice: '$6.333', todayPrice: '$633', countryName: 'Uruguay' },
  MX: { totalPrice: '$3,238.72', todayPrice: '$323.87', countryName: 'México' },
  CO: {
    totalPrice: '$595.160',
    todayPrice: '$59.516',
    countryName: 'Colombia',
  },
  AR: {
    totalPrice: '$242.027',
    todayPrice: '$24.202',
    countryName: 'Argentina',
  },
  PE: { totalPrice: 'S/ 554.00', todayPrice: 'S/ 55.40', countryName: 'Perú' },
  CL: { totalPrice: '$167.570', todayPrice: '$16.757', countryName: 'Chile' },
  PY: {
    totalPrice: 'Gs. 1.018.362',
    todayPrice: 'Gs. 101.836',
    countryName: 'Paraguay',
  },
  PA: {
    totalPrice: 'B/. 159.00',
    todayPrice: 'B/. 15.90',
    countryName: 'Panamá',
  },
  CR: {
    totalPrice: '₡75.334',
    todayPrice: '₡7.533',
    countryName: 'Costa Rica',
  },
  PR: {
    totalPrice: '$149.99',
    todayPrice: '$14.99',
    countryName: 'Puerto Rico',
  },
  EC: { totalPrice: '$149.99', todayPrice: '$14.99', countryName: 'Ecuador' },
  BO: {
    totalPrice: 'Bs 1.099,00',
    todayPrice: 'Bs 109,90',
    countryName: 'Bolivia',
  },
  SV: {
    totalPrice: '$149.99',
    todayPrice: '$14.99',
    countryName: 'El Salvador',
  },
  GT: {
    totalPrice: 'Q 1,214.00',
    todayPrice: 'Q 121.40',
    countryName: 'Guatemala',
  },
  ES: { totalPrice: '142 €', todayPrice: '14,25 €', countryName: 'España' },
  US: {
    totalPrice: 'USD 149,99',
    todayPrice: 'USD 14,99',
    countryName: 'Estados Unidos',
  },
}

const DEFAULT_PRICING: CountryPricing = {
  totalPrice: 'USD 149,99',
  todayPrice: 'USD 14,99',
  countryName: '',
  countryCode: 'US',
  loading: false,
}

// Module-level cache so the fetch only happens once across all hook instances
let cachedPricing: CountryPricing | null = null
let fetchPromise: Promise<CountryPricing> | null = null

async function fetchCountryPricing(): Promise<CountryPricing> {
  try {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 4000)
    const response = await fetch('https://ipapi.co/json/', {
      signal: controller.signal,
    })
    clearTimeout(timeout)

    if (!response.ok) throw new Error('fail')
    const data = await response.json()
    const countryCode: string = data.country_code || 'US'
    const match = PRICING_MAP[countryCode]

    if (match) {
      return {
        totalPrice: match.totalPrice,
        todayPrice: match.todayPrice,
        countryName: match.countryName,
        countryCode,
        loading: false,
      }
    }
  } catch {
    // silently fall through to default
  }
  return DEFAULT_PRICING
}

/**
 * Returns country-based pricing. Shows USD immediately.
 * Pass a ref to the pricing section — geolocation only triggers
 * when that element enters the viewport (or is about to).
 */
export function useCurrencyDetection(
  triggerRef?: React.RefObject<HTMLElement>,
): CountryPricing {
  const [pricing, setPricing] = useState<CountryPricing>(
    cachedPricing || DEFAULT_PRICING,
  )
  const fetched = useRef(false)

  useEffect(() => {
    // If already cached, apply immediately
    if (cachedPricing) {
      setPricing(cachedPricing)
      return
    }

    // If no ref provided, fetch after idle
    if (!triggerRef?.current) {
      const run = () => {
        if (fetched.current) return
        fetched.current = true
        if (!fetchPromise) fetchPromise = fetchCountryPricing()
        fetchPromise.then((result) => {
          cachedPricing = result
          setPricing(result)
        })
      }

      if ('requestIdleCallback' in window) {
        const id = (window as any).requestIdleCallback(run)
        return () => (window as any).cancelIdleCallback(id)
      } else {
        const id = setTimeout(run, 2000)
        return () => clearTimeout(id)
      }
    }

    // Use IntersectionObserver to trigger fetch when section is near viewport
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !fetched.current) {
          fetched.current = true
          observer.disconnect()
          if (!fetchPromise) fetchPromise = fetchCountryPricing()
          fetchPromise.then((result) => {
            cachedPricing = result
            setPricing(result)
          })
        }
      },
      { rootMargin: '400px' }, // Start fetching 400px before it's visible
    )

    observer.observe(triggerRef.current)
    return () => observer.disconnect()
  }, [triggerRef])

  return pricing
}
